import { useState, useCallback } from 'react';
import { Box, Button } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import {
  DataGridPro,
  GridPagination,
  GridSortDirection,
} from '@mui/x-data-grid-pro';
import { useStableGridModels } from '@common/hooks';
import { ConfirmationModal, TableNoResultPlaceholder } from '@components';
import { useDeleteTimeOffMutation } from '@common/cache';
import { toast } from 'react-toastify';
import { formatApiError } from 'common/errors/error-formatting.ts';
import { checkUserPermission } from '@common/auth';
import useUserStore from 'store/user.store';
import {
  PinnedColumnMenu,
  TableColumnHeightMapper,
} from 'components/TablePreferences';
import { ColumnDensity } from '@common/types';
import {
  ITimeOffTableEntry,
  ITimeOffTableFilters,
  PersistedTimeOffColumn,
  initialTimeOffColumns,
  useTimeOffTable,
} from '../hooks';
import { GridPinnedColumnFields } from '@mui/x-data-grid-pro';

interface ITimeOffTableProps {
  rows: ITimeOffTableEntry[];
  filters: ITimeOffTableFilters;
  setFilters: (filters: ITimeOffTableFilters) => void;
  totalCount: number;
  loading: boolean;
  disabled?: boolean;
  hiddenColumns: string[];
  setHiddenColumns: (hiddenColumns: string[]) => void;
  density: ColumnDensity;
  setDensity: (density: ColumnDensity) => void;
  onResetFilters: () => void;
}

export const TimeOffTable = ({
  rows,
  filters,
  setFilters,
  totalCount,
  loading,
  disabled = false,
  hiddenColumns,
  setHiddenColumns,
  density,
  setDensity,
  onResetFilters,
}: ITimeOffTableProps) => {
  const { user } = useUserStore();
  const hasEditPermission = checkUserPermission(user, 'manage_time_off');

  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [persistedColumns, setPersistedColumns] = useState<
    PersistedTimeOffColumn[]
  >(initialTimeOffColumns);
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumnFields>({
    left: [],
    right: [],
  });

  const onDeleteClick = useCallback((id: string) => {
    setPendingDeleteId(id);
  }, []);

  const handleResetView = () => {
    setPersistedColumns(initialTimeOffColumns);
    setPinnedColumns({ left: [], right: [] });
    setHiddenColumns([]);
    setDensity('comfortable');
    onResetFilters();
    setIsResetModalOpen(false);
  };

  const {
    handleChangeOrder,
    onPageModelChange,
    columns,
    handleColumnOrderChange,
    handleColumnWidthChange,
    handlePinnedColumnsChange,
    columnVisibilityModel,
    handleColumnVisibilityModelChange,
    initialSortFilters,
    apiRef,
  } = useTimeOffTable({
    filters,
    setFilters,
    persistedColumns,
    setPersistedColumns,
    pinnedColumns,
    setPinnedColumns,
    hiddenColumns,
    setHiddenColumns,
    density,
    disabled,
    hasEditPermission,
    onDeleteClick,
  });

  const { paginationModel, sortModel, rowCount } = useStableGridModels({
    page: filters.page,
    limit: filters.limit,
    sort_by: filters.sort_by,
    sort_order: filters.sort_order,
    totalCount,
    loading,
  });

  const deleteMutation = useDeleteTimeOffMutation(pendingDeleteId ?? '');

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync();
      setPendingDeleteId(null);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        overflow: 'auto',
      }}
    >
      <DataGridPro
        apiRef={apiRef}
        sx={{
          maxHeight: 'calc(100vh - 460px)',
          '--DataGrid-overlayHeight': 'calc(100vh - 460px)',
          borderRadius: 0,
          boxShadow: 'none',
          border: theme => `1px solid ${theme.palette.grey[300]}`,
          borderBottom: 'none',
          '& .MuiDataGrid-cell': { alignItems: 'center', display: 'flex' },
          '& .MuiDataGrid-row': { cursor: 'default' },
        }}
        slots={{
          columnMenu: props => (
            <PinnedColumnMenu {...props} pinnedColumns={pinnedColumns} />
          ),
          pagination: props => (
            <Box
              sx={{
                px: 2,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Button
                color='primary'
                sx={{ whiteSpace: 'nowrap' }}
                startIcon={<Refresh color='primary' />}
                onClick={() => setIsResetModalOpen(true)}
              >
                RESET VIEW
              </Button>
              <GridPagination {...props} />
            </Box>
          ),
          noRowsOverlay: () => (
            <Box
              sx={{
                height: '100%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: theme => theme.palette.common.white,
              }}
            >
              <TableNoResultPlaceholder />
            </Box>
          ),
        }}
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={handlePinnedColumnsChange}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnWidthChange={handleColumnWidthChange}
        getRowHeight={() => TableColumnHeightMapper[density ?? 'comfortable']}
        pageSizeOptions={[10, 25, 50, 100]}
        pagination
        paginationModel={paginationModel}
        rows={rows}
        columns={columns}
        rowCount={rowCount}
        loading={loading}
        disableMultipleColumnsSorting
        disableRowSelectionOnClick
        paginationMode='server'
        sortingMode='server'
        disableColumnFilter
        disableColumnSelector
        disableColumnReorder
        sortModel={sortModel}
        onPaginationModelChange={onPageModelChange}
        onSortModelChange={model => {
          if (
            model[0] &&
            (model[0].field !== filters.sort_by ||
              model[0].sort !== filters.sort_order)
          ) {
            handleChangeOrder(model[0].field, model[0].sort);
          } else {
            handleChangeOrder(
              initialSortFilters.sort_by,
              initialSortFilters.sort_order as GridSortDirection,
            );
          }
        }}
        getRowClassName={params =>
          params.indexRelativeToCurrentPage % 2 !== 0 ? 'row-even' : ''
        }
      />

      <ConfirmationModal
        title='Delete time off'
        message='Time off will be deleted and all details will be lost. Are you sure you want to continue?'
        onConfirm={handleDelete}
        onClose={() => setPendingDeleteId(null)}
        isOpen={pendingDeleteId !== null}
        isLoading={deleteMutation.isPending}
        confirmText='DELETE'
      />
      <ConfirmationModal
        title='Reset table view'
        message='This will restore the default columns, sorting, and filters. Your custom layout and pinned columns will be cleared.'
        closeText='KEEP CURRENT VIEW'
        confirmText='RESET VIEW'
        onConfirm={handleResetView}
        onClose={() => setIsResetModalOpen(false)}
        isOpen={isResetModalOpen}
        reverseButtonOrder
      />
    </Box>
  );
};
